import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  Grid,
  Typography,
} from "@mui/material";
import EditText from "../../components/editText";
import { useLazyGetCmsQuery, useUpdateCmsMutation } from "../../services/cms";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import PhoneInput from "react-phone-input-2";
import useTranslation from "../../hooks/traslation";
import usePermission from "../../hooks/usePermission";

function CustomTabPanel(props: any) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const [getCms] = useLazyGetCmsQuery();
  const [updateCMS] = useUpdateCmsMutation();
  const [cmsId, setCmsId] = useState("");
  const [phoneCode, setPhoneCode] = useState("+91");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [valueTabs, setValueTabs] = useState(0);
  const { isAdd } = usePermission({
    label: "Manage CMS",
    page_permission: "isView",
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .required(translation.error.emailReq)
      .test(
        "no-whitespace",
        "Email cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      )
      .matches(
        /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
        translation.error.validEmail
      ),
    phone: Yup.string()
      .required(translation.error.phoneReq)
      .test(
        "no-whitespace",
        "Phone cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      )
      .min(6, translation.error.minPhone)
      .max(16, translation.error.maxPhone),
    aboutUs: Yup.string()
      .required("About Us (English) is required")
      .test(
        "no-whitespace",
        "About Us cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
    aboutUs_ar: Yup.string()
      .required("About Us (Arabic) is required")
      .test(
        "no-whitespace",
        "About Us (Arabic) cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
    privacyPolicy: Yup.string()
      .required("Privacy Policy (English) is required")
      .test(
        "no-whitespace",
        "Privacy Policy cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
    privacyPolicy_ar: Yup.string()
      .required("Privacy Policy (Arabic) is required")
      .test(
        "no-whitespace",
        "Privacy Policy (Arabic) cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
    termCondition: Yup.string()
      .required("Terms & Conditions (English) is required")
      .test(
        "no-whitespace",
        "Terms & Conditions cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
    termCondition_ar: Yup.string()
      .required("Terms & Conditions (Arabic) is required")
      .test(
        "no-whitespace",
        "Terms & Conditions (Arabic) cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      phone: "",
      aboutUs: "",
      aboutUs_ar: "",
      privacyPolicy: "",
      privacyPolicy_ar: "",
      termCondition: "",
      termCondition_ar: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const body = {
          email: values.email?.trim(),
          dialCode: (phoneCode.includes("+") ? "" : "+") + phoneCode,
          phone: values.phone?.trim(),
          privacyPolicy: values.privacyPolicy?.trim(),
          privacyPolicy_ar: values.privacyPolicy_ar?.trim(),
          aboutUs: values.aboutUs?.trim(),
          aboutUs_ar: values.aboutUs_ar?.trim(),
          legalPolicy: values.termCondition?.trim(),
          legalPolicy_ar: values.termCondition_ar?.trim(),
          id: cmsId || "",
        };
        const response = await updateCMS(body).unwrap();
        if (response.statusCode === 200) {
          showToast(translation.error.contactUpdated);
        }
      } catch (error: any) {
        showError(error?.data?.message);
      }
    },
  });

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    setPhoneDisplay(country?.dialCode + phone);
    setPhoneCode(country?.dialCode);
  };

  useEffect(() => {
    const getCmsData = async () => {
      try {
        const response = await getCms({}).unwrap();
        if (response.statusCode === 200) {
          setCmsId(response?.data?._id || "");
          formik.setValues({
            email: response.data?.email || "",
            phone: response?.data?.phone || "",
            aboutUs: response?.data?.aboutUs || "",
            aboutUs_ar: response?.data?.aboutUs_ar || "",
            privacyPolicy: response?.data?.privacyPolicy || "",
            privacyPolicy_ar: response?.data?.privacyPolicy_ar || "",
            termCondition: response?.data?.legalPolicy || "",
            termCondition_ar: response?.data?.legalPolicy_ar || "",
          });
          setPhoneCode(response?.data?.dialCode || "+91");
          setPhoneDisplay(
            (response?.data?.dialCode || "") + (response?.data?.phone || "")
          );
        }
      } catch (error) {}
    };
    getCmsData();
    // eslint-disable-next-line
  }, []);

  const handleSaveContact = async () => {
    await formik.setTouched({ email: true, phone: true });
    await formik.validateForm();
    const { email, phone } = formik.errors;
    if (email || phone) {
      showError("Please enter valid values for Contact fields.");
      return;
    }
    try {
      const body = {
        email: formik.values.email?.trim(),
        dialCode: (phoneCode.includes("+") ? "" : "+") + phoneCode,
        phone: formik.values.phone?.trim(),
        privacyPolicy: formik.values.privacyPolicy?.trim(),
        privacyPolicy_ar: formik.values.privacyPolicy_ar?.trim(),
        aboutUs: formik.values.aboutUs?.trim(),
        aboutUs_ar: formik.values.aboutUs_ar?.trim(),
        legalPolicy: formik.values.termCondition?.trim(),
        legalPolicy_ar: formik.values.termCondition_ar?.trim(),
        id: cmsId || "",
      };
      const response = await updateCMS(body).unwrap();
      if (response.statusCode === 200) {
        showToast("Contact updated");
      }
    } catch (error: any) {
      showError(error?.data?.message);
    }
  };

  const handleSaveAbout = async () => {
    await formik.setTouched({ aboutUs: true, aboutUs_ar: true });
    await formik.validateForm();
    const { aboutUs, aboutUs_ar } = formik.errors;
    if (aboutUs || aboutUs_ar) {
      showError("Please enter valid values for About Us fields.");
      return;
    }
    try {
      const body = {
        email: formik.values.email?.trim(),
        dialCode: (phoneCode.includes("+") ? "" : "+") + phoneCode,
        phone: formik.values.phone?.trim(),
        privacyPolicy: formik.values.privacyPolicy?.trim(),
        privacyPolicy_ar: formik.values.privacyPolicy_ar?.trim(),
        aboutUs: formik.values.aboutUs?.trim(),
        aboutUs_ar: formik.values.aboutUs_ar?.trim(),
        legalPolicy: formik.values.termCondition?.trim(),
        legalPolicy_ar: formik.values.termCondition_ar?.trim(),
        id: cmsId || "",
      };
      const response = await updateCMS(body).unwrap();
      if (response.statusCode === 200) {
        showToast("About Us updated");
      }
    } catch (error: any) {
      showError(error?.data?.message);
    }
  };

  const handleSavePrivacy = async () => {
    await formik.setTouched({ privacyPolicy: true, privacyPolicy_ar: true });
    await formik.validateForm();
    const { privacyPolicy, privacyPolicy_ar } = formik.errors;
    if (privacyPolicy || privacyPolicy_ar) {
      showError("Please enter valid values for Privacy Policy fields.");
      return;
    }
    try {
      const body = {
        email: formik.values.email?.trim(),
        dialCode: (phoneCode.includes("+") ? "" : "+") + phoneCode,
        phone: formik.values.phone?.trim(),
        privacyPolicy: formik.values.privacyPolicy?.trim(),
        privacyPolicy_ar: formik.values.privacyPolicy_ar?.trim(),
        aboutUs: formik.values.aboutUs?.trim(),
        aboutUs_ar: formik.values.aboutUs_ar?.trim(),
        legalPolicy: formik.values.termCondition?.trim(),
        legalPolicy_ar: formik.values.termCondition_ar?.trim(),
        id: cmsId || "",
      };
      const response = await updateCMS(body).unwrap();
      if (response.statusCode === 200) {
        showToast("Privacy Policy updated");
      }
    } catch (error: any) {
      showError(error?.data?.message);
    }
  };

  const handleSaveTerms = async () => {
    await formik.setTouched({ termCondition: true, termCondition_ar: true });
    await formik.validateForm();
    const { termCondition, termCondition_ar } = formik.errors;
    if (termCondition || termCondition_ar) {
      showError("Please enter valid values for Terms & Condition fields.");
      return;
    }
    try {
      const body = {
        email: formik.values.email?.trim(),
        dialCode: (phoneCode.includes("+") ? "" : "+") + phoneCode,
        phone: formik.values.phone?.trim(),
        privacyPolicy: formik.values.privacyPolicy?.trim(),
        privacyPolicy_ar: formik.values.privacyPolicy_ar?.trim(),
        aboutUs: formik.values.aboutUs?.trim(),
        aboutUs_ar: formik.values.aboutUs_ar?.trim(),
        legalPolicy: formik.values.termCondition?.trim(),
        legalPolicy_ar: formik.values.termCondition_ar?.trim(),
        id: cmsId || "",
      };
      const response = await updateCMS(body).unwrap();
      if (response.statusCode === 200) {
        showToast("Terms & Condition updated");
      }
    } catch (error: any) {
      showError(error?.data?.message);
    }
  };

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{translation.main.manageCms}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translation.dashboard.dashboard}
          </span>{" "}
          - {translation.main.manageCms}
        </p>
      </div>
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label={translation.main.customerSupport} {...a11yProps(0)} />
              <Tab label={translation.main.aboutUs} {...a11yProps(1)} />
              <Tab label={translation.main.privacyPolicy} {...a11yProps(2)} />
              <Tab label={"Term and Condition"} {...a11yProps(3)} />
            </Tabs>
          </Box>
        </div>
      </div>
      <div className="cards">
        <form onSubmit={formik.handleSubmit}>
          <CustomTabPanel value={valueTabs} index={0}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label>{translation.auth.emailId}</label>
                <TextField
                  hiddenLabel
                  className="text_field"
                  type={"email"}
                  name="email"
                  variant="outlined"
                  fullWidth
                  value={formik.values.email}
                  onChange={(e) => {
                    if (e.target.value.length <= 50) {
                      formik.handleChange(e);
                    } else {
                      showError("Email can't exceed 50 characters");
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  placeholder={translation.auth.emailId}
                  required
                />
              </div>
              <div className="control_group w_50">
                <label>{translation.auth.phone}</label>
                <PhoneInput
                  country={"us"}
                  placeholder="0 (000) 000-000"
                  enableSearch={true}
                  value={phoneDisplay}
                  onChange={(phone, country) =>
                    handleChangePhone(phone, country)
                  }
                  inputStyle={{ width: "100%" }}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <h6 className="err_msg">{formik.errors.phone}</h6>
                )}
              </div>
            </div>
            <div className="form_btn">
              {isAdd && (
                <Button
                  size="large"
                  type="button"
                  className="btn btn_primary"
                  onClick={handleSaveContact}
                >
                  {translation.globals.save}
                </Button>
              )}
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={1}>
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {translation.main.aboutUsEng}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.aboutUs}
                  setContent={(content) =>
                    formik.setFieldValue("aboutUs", content)
                  }
                />
                {formik.touched.aboutUs && formik.errors.aboutUs && (
                  <h6 className="err_msg">{formik.errors.aboutUs}</h6>
                )}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {translation.main.aboutUsAr}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.aboutUs_ar}
                  setContent={(content) =>
                    formik.setFieldValue("aboutUs_ar", content)
                  }
                />
                {formik.touched.aboutUs_ar && formik.errors.aboutUs_ar && (
                  <h6 className="err_msg">{formik.errors.aboutUs_ar}</h6>
                )}
              </Grid>
            </Grid>
            <div className="form_btn">
              {isAdd && (
                <Button
                  size="large"
                  type="button"
                  className="btn btn_primary"
                  onClick={handleSaveAbout}
                >
                  {translation.globals.save}
                </Button>
              )}
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={2}>
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {translation.main.privacyEng}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.privacyPolicy}
                  setContent={(content) =>
                    formik.setFieldValue("privacyPolicy", content)
                  }
                />
                {formik.touched.privacyPolicy &&
                  formik.errors.privacyPolicy && (
                    <h6 className="err_msg">{formik.errors.privacyPolicy}</h6>
                  )}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {translation.main.privacyAr}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.privacyPolicy_ar}
                  setContent={(content) =>
                    formik.setFieldValue("privacyPolicy_ar", content)
                  }
                />
                {formik.touched.privacyPolicy_ar &&
                  formik.errors.privacyPolicy_ar && (
                    <h6 className="err_msg">
                      {formik.errors.privacyPolicy_ar}
                    </h6>
                  )}
              </Grid>
            </Grid>
            <div className="form_btn">
              {isAdd && (
                <Button
                  size="large"
                  type="button"
                  className="btn btn_primary"
                  onClick={handleSavePrivacy}
                >
                  {translation.globals.save}
                </Button>
              )}
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={3}>
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {"Term and Condition"} <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.termCondition}
                  setContent={(content) =>
                    formik.setFieldValue("termCondition", content)
                  }
                />
                {formik.touched.termCondition &&
                  formik.errors.termCondition && (
                    <h6 className="err_msg">{formik.errors.termCondition}</h6>
                  )}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className="custom_label">
                  {"Term and Condition"} <span style={{ color: "red" }}>*</span>
                </Typography>
                <EditText
                  content={formik.values.termCondition_ar}
                  setContent={(content) =>
                    formik.setFieldValue("termCondition_ar", content)
                  }
                />
                {formik.touched.termCondition_ar &&
                  formik.errors.termCondition_ar && (
                    <h6 className="err_msg">
                      {formik.errors.termCondition_ar}
                    </h6>
                  )}
              </Grid>
            </Grid>
            <div className="form_btn">
              {isAdd && (
                <Button
                  size="large"
                  type="button"
                  className="btn btn_primary"
                  onClick={handleSaveTerms}
                >
                  {translation.globals.save}
                </Button>
              )}
            </div>
          </CustomTabPanel>
        </form>
      </div>
    </MainContainer>
  );
};

export default ManageCms;
